<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import schedulingAPI from '../api/scheduling';
import PatientOverviewModal from './PatientOverviewModal.vue';

const props = defineProps({
  initialPatients: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['saved']);

const patients = ref(props.initialPatients && props.initialPatients.length ? [...props.initialPatients] : []);
const isLoading = ref(false);
const searchQuery = ref('');
const activeStatusFilter = ref('all'); // 'all' | 'active' | 'completed' | 'inactive' | 'blocked'

watch(
  () => props.initialPatients,
  newVal => {
    if (newVal && newVal.length && (!patients.value.length || !searchQuery.value)) {
      patients.value = [...newVal];
    }
  }
);

const showModal = ref(false);
const showProntuarioModal = ref(false);
const showDischargeModal = ref(false);
const showBlockModal = ref(false);
const showOverviewModal = ref(false);
const overviewPatientData = ref(null);
const isSaving = ref(false);

const selectedPatient = ref(null);
const dischargeNotes = ref('');
const blockReason = ref('');

const formData = ref({
  id: null,
  name: '',
  email: '',
  phone_number: '',
  document_number: '',
  birth_date: '',
  medical_record: '',
  notes: '',
});

const fetchPatients = async () => {
  isLoading.value = true;
  try {
    const params = {};
    if (searchQuery.value.trim()) {
      params.query = searchQuery.value.trim();
    }
    const res = await schedulingAPI.getPatients(params);
    patients.value = Array.isArray(res.data) ? res.data : (res.data?.patients || []);
  } catch (err) {
    console.error('Erro ao carregar pacientes:', err);
  } finally {
    isLoading.value = false;
  }
};

// Contadores de Status para as Abas
const statusCounts = computed(() => {
  const counts = {
    all: patients.value.length,
    active: 0,
    completed: 0,
    inactive: 0,
    blocked: 0,
  };

  patients.value.forEach(p => {
    if (p.is_blocked || p.computed_status === 'blocked') {
      counts.blocked += 1;
    } else if (p.discharged_at || p.computed_status === 'discharged' || p.computed_status === 'completed_recent') {
      counts.completed += 1;
    } else if (p.computed_status === 'active') {
      counts.active += 1;
    } else {
      counts.inactive += 1;
    }
  });

  return counts;
});

// Pacientes filtrados pela aba de status ativa e pela busca
const filteredPatients = computed(() => {
  let list = patients.value;

  if (activeStatusFilter.value !== 'all') {
    list = list.filter(p => {
      const isBlocked = p.is_blocked || p.computed_status === 'blocked';
      const isCompleted = p.discharged_at || p.computed_status === 'discharged' || p.computed_status === 'completed_recent';
      const isActive = p.computed_status === 'active' && !isBlocked && !isCompleted;

      if (activeStatusFilter.value === 'blocked') return isBlocked;
      if (activeStatusFilter.value === 'completed') return isCompleted && !isBlocked;
      if (activeStatusFilter.value === 'active') return isActive;
      if (activeStatusFilter.value === 'inactive') return !isActive && !isCompleted && !isBlocked;
      return true;
    });
  }

  return list;
});

const openPatientOverview = async p => {
  try {
    const res = await schedulingAPI.getPatient(p.id);
    overviewPatientData.value = res.data;
    showOverviewModal.value = true;
  } catch (err) {
    console.error('Erro ao carregar detalhes do paciente:', err);
    alert('Erro ao carregar dados do paciente.');
  }
};

const handleOverviewUpdated = async () => {
  if (overviewPatientData.value?.id) {
    const res = await schedulingAPI.getPatient(overviewPatientData.value.id);
    overviewPatientData.value = res.data;
  }
  await fetchPatients();
  emit('saved');
};

const handleOverviewEditPatient = p => {
  showOverviewModal.value = false;
  openEditPatient(p);
};

const handleOverviewDischarge = p => {
  showOverviewModal.value = false;
  openDischargeModal(p);
};

const handleOverviewBlock = p => {
  showOverviewModal.value = false;
  openBlockModal(p);
};

const openNewPatient = () => {
  formData.value = {
    id: null,
    name: '',
    email: '',
    phone_number: '',
    document_number: '',
    birth_date: '',
    medical_record: '',
    notes: '',
  };
  showModal.value = true;
};

const openEditPatient = p => {
  formData.value = {
    id: p.id,
    name: p.name || '',
    email: p.email || '',
    phone_number: p.phone_number || '',
    document_number: p.document_number || '',
    birth_date: p.birth_date || '',
    medical_record: p.medical_record || '',
    notes: p.notes || '',
  };
  showModal.value = true;
};

const openProntuario = async p => {
  try {
    const res = await schedulingAPI.getPatient(p.id);
    selectedPatient.value = res.data;
    showProntuarioModal.value = true;
  } catch (err) {
    alert('Erro ao carregar prontuário do paciente.');
  }
};

const openDischargeModal = p => {
  selectedPatient.value = p;
  dischargeNotes.value = p.discharged_notes || '';
  showDischargeModal.value = true;
};

const handleConfirmDischarge = async () => {
  if (!selectedPatient.value) return;
  isSaving.value = true;
  try {
    await schedulingAPI.dischargePatient(selectedPatient.value.id, {
      notes: dischargeNotes.value.trim(),
    });
    showDischargeModal.value = false;
    await fetchPatients();
    emit('saved');
  } catch (err) {
    alert('Erro ao registrar alta clínica.');
  } finally {
    isSaving.value = false;
  }
};

const handleReopenTreatment = async p => {
  if (!confirm(`Reabrir o tratamento clínico de "${p.name}"?`)) return;
  isSaving.value = true;
  try {
    await schedulingAPI.reopenPatient(p.id);
    showDischargeModal.value = false;
    await fetchPatients();
    emit('saved');
  } catch (err) {
    alert('Erro ao reabrir tratamento.');
  } finally {
    isSaving.value = false;
  }
};

const openBlockModal = p => {
  selectedPatient.value = p;
  blockReason.value = p.blocked_reason || 'Inadimplência ou restrição cadastrada pela clínica';
  showBlockModal.value = true;
};

const handleConfirmBlock = async () => {
  if (!selectedPatient.value) return;
  isSaving.value = true;
  try {
    await schedulingAPI.blockPatient(selectedPatient.value.id, {
      reason: blockReason.value.trim(),
    });
    showBlockModal.value = false;
    await fetchPatients();
    emit('saved');
  } catch (err) {
    alert('Erro ao bloquear paciente.');
  } finally {
    isSaving.value = false;
  }
};

const handleUnblock = async p => {
  if (!confirm(`Remover restrição e desbloquear o paciente "${p.name}"?`)) return;
  isSaving.value = true;
  try {
    await schedulingAPI.unblockPatient(p.id);
    await fetchPatients();
    emit('saved');
  } catch (err) {
    alert('Erro ao desbloquear paciente.');
  } finally {
    isSaving.value = false;
  }
};

const savePatient = async () => {
  if (!formData.value.name.trim()) {
    alert('Por favor, informe o nome do paciente.');
    return;
  }

  isSaving.value = true;
  try {
    if (formData.value.id) {
      await schedulingAPI.updatePatient(formData.value.id, formData.value);
    } else {
      await schedulingAPI.createPatient(formData.value);
    }
    showModal.value = false;
    await fetchPatients();
    emit('saved');
  } catch (err) {
    alert('Erro ao salvar paciente.');
  } finally {
    isSaving.value = false;
  }
};

const deletePatient = async p => {
  if (!confirm(`Tem certeza que deseja excluir o paciente "${p.name}"?`)) return;

  try {
    await schedulingAPI.deletePatient(p.id);
    await fetchPatients();
    emit('saved');
  } catch (err) {
    alert('Não é possível excluir paciente com agendamentos vinculados.');
  }
};

const saveProntuarioChanges = async () => {
  if (!selectedPatient.value) return;

  isSaving.value = true;
  try {
    await schedulingAPI.updatePatient(selectedPatient.value.id, {
      medical_record: selectedPatient.value.medical_record,
    });
    alert('Prontuário salvo com sucesso!');
    showProntuarioModal.value = false;
    await fetchPatients();
  } catch (err) {
    alert('Erro ao atualizar prontuário.');
  } finally {
    isSaving.value = false;
  }
};

watch(searchQuery, () => {
  fetchPatients();
});

onMounted(() => {
  if (patients.value.length === 0) {
    fetchPatients();
  }
});
</script>

<template>
  <div class="space-y-4 select-none">
    <!-- Header / Actions -->
    <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div>
        <h2 class="text-base font-semibold text-n-slate-12 flex items-center gap-2">
          <span>Pacientes</span>
        </h2>
        <p class="text-xs text-n-slate-11">
          Gerencie a base de pacientes clínicos, prontuários, status de tratamento e histórico de atendimentos.
        </p>
      </div>

      <div class="flex items-center gap-2.5">
        <!-- Search Input Pill (Flex Container: No text overlap, No password manager collision) -->
        <div class="inline-flex items-center gap-2 h-8 px-2.5 bg-n-alpha-1 hover:bg-n-alpha-2 border border-n-weak focus-within:border-brand focus-within:ring-1 focus-within:ring-brand rounded-lg w-64 sm:w-72 transition-all">
          <svg
            class="w-3.5 h-3.5 text-n-slate-10 shrink-0 pointer-events-none"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            v-model="searchQuery"
            type="search"
            autocomplete="off"
            spellcheck="false"
            placeholder="Buscar por nome, telefone ou e-mail..."
            class="w-full !bg-transparent !border-0 !p-0 !m-0 !outline-none text-xs text-n-slate-12 placeholder:text-n-slate-10 focus:ring-0"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="text-n-slate-10 hover:text-n-slate-12 cursor-pointer p-0.5 shrink-0"
            title="Limpar busca"
            @click="searchQuery = ''"
          >
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <button
          type="button"
          class="h-8 px-3.5 bg-brand hover:bg-brand-hover active:bg-brand text-white text-xs font-medium rounded-lg shadow-xs transition-all cursor-pointer shrink-0 flex items-center gap-1.5"
          @click="openNewPatient"
        >
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          <span>Novo Paciente</span>
        </button>
      </div>
    </div>

    <!-- Abas de Filtros de Status (Chatwoot v4 TabBar Pattern) -->
    <div class="flex items-center gap-0.5 p-0.5 bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak/60 rounded-lg overflow-x-auto max-w-full">
      <button
        type="button"
        class="px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
        :class="activeStatusFilter === 'all' ? 'bg-n-solid-active shadow-xs text-n-brand font-semibold' : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2/50'"
        @click="activeStatusFilter = 'all'"
      >
        <span>Todos</span>
        <span class="text-[10px] px-1.5 py-0.2 bg-n-alpha-2 rounded-full text-n-slate-11 font-mono">{{ statusCounts.all }}</span>
      </button>

      <button
        type="button"
        class="px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
        :class="activeStatusFilter === 'active' ? 'bg-n-solid-active shadow-xs text-emerald-600 dark:text-emerald-400 font-semibold' : 'text-n-slate-10 hover:text-emerald-600 dark:hover:text-emerald-400 hover:bg-n-alpha-2/50'"
        @click="activeStatusFilter = 'active'"
      >
        <span class="w-2 h-2 rounded-full bg-emerald-500 shrink-0"></span>
        <span>Ativos</span>
        <span class="text-[10px] px-1.5 py-0.2 bg-emerald-500/15 rounded-full text-emerald-700 dark:text-emerald-300 font-mono">{{ statusCounts.active }}</span>
      </button>

      <button
        type="button"
        class="px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
        :class="activeStatusFilter === 'completed' ? 'bg-n-solid-active shadow-xs text-cyan-600 dark:text-cyan-400 font-semibold' : 'text-n-slate-10 hover:text-cyan-600 dark:hover:text-cyan-400 hover:bg-n-alpha-2/50'"
        @click="activeStatusFilter = 'completed'"
      >
        <span class="w-2 h-2 rounded-full bg-cyan-500 shrink-0"></span>
        <span>Concluídos / Alta</span>
        <span class="text-[10px] px-1.5 py-0.2 bg-cyan-500/15 rounded-full text-cyan-700 dark:text-cyan-300 font-mono">{{ statusCounts.completed }}</span>
      </button>

      <button
        type="button"
        class="px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
        :class="activeStatusFilter === 'inactive' ? 'bg-n-solid-active shadow-xs text-amber-600 dark:text-amber-400 font-semibold' : 'text-n-slate-10 hover:text-amber-600 dark:hover:text-amber-400 hover:bg-n-alpha-2/50'"
        @click="activeStatusFilter = 'inactive'"
      >
        <span class="w-2 h-2 rounded-full bg-amber-500 shrink-0"></span>
        <span>Inativos</span>
        <span class="text-[10px] px-1.5 py-0.2 bg-amber-500/15 rounded-full text-amber-700 dark:text-amber-300 font-mono">{{ statusCounts.inactive }}</span>
      </button>

      <button
        type="button"
        class="px-3 py-1 rounded-md text-xs font-medium transition-all cursor-pointer flex items-center gap-1.5 shrink-0"
        :class="activeStatusFilter === 'blocked' ? 'bg-n-solid-active shadow-xs text-rose-600 dark:text-rose-400 font-semibold' : 'text-n-slate-10 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-n-alpha-2/50'"
        @click="activeStatusFilter = 'blocked'"
      >
        <span class="w-2 h-2 rounded-full bg-rose-500 shrink-0"></span>
        <span>Bloqueados</span>
        <span class="text-[10px] px-1.5 py-0.2 bg-rose-500/15 rounded-full text-rose-700 dark:text-rose-300 font-mono">{{ statusCounts.blocked }}</span>
      </button>
    </div>

    <!-- Patients Table -->
    <div class="rounded-xl border border-n-weak bg-n-surface-1 overflow-hidden shadow-xs">
      <div v-if="isLoading" class="flex justify-center items-center py-16">
        <div class="animate-spin rounded-full h-7 w-7 border-2 border-brand border-t-transparent"></div>
      </div>

      <div v-else-if="filteredPatients.length === 0" class="p-12 text-center text-n-slate-11 space-y-1">
        <p class="text-sm font-semibold text-n-slate-12">Nenhum paciente encontrado neste filtro.</p>
        <p class="text-xs text-n-slate-10">Tente buscar por outro termo ou selecione a aba "Todos".</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-left text-xs min-w-[700px]">
          <thead class="bg-n-alpha-1 border-b border-n-weak text-[11px] font-medium uppercase text-n-slate-11 tracking-wider">
            <tr>
              <th class="py-2.5 px-3.5">Paciente</th>
              <th class="py-2.5 px-3.5">Status Clínico</th>
              <th class="py-2.5 px-3.5">Contato</th>
              <th class="py-2.5 px-3.5">Sessões / Matrículas</th>
              <th class="py-2.5 px-3.5 text-right">Ações</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="p in filteredPatients"
              :key="p.id"
              class="hover:bg-n-alpha-1/50 transition-colors"
              :class="{ 'bg-rose-500/5': p.is_blocked }"
            >
              <!-- Nome / Avatar (Clicáveis para Visão 360) -->
              <td class="py-2.5 px-3.5">
                <div class="flex items-center gap-2.5">
                  <button
                    type="button"
                    class="w-7.5 h-7.5 rounded-lg font-semibold flex items-center justify-center text-[11px] shrink-0 cursor-pointer hover:ring-2 hover:ring-brand/40 transition-all shadow-xs"
                    :class="p.is_blocked ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30' : 'bg-brand-subtle text-n-brand border border-brand/20 hover:bg-brand hover:text-white'"
                    title="Ver histórico e visão 360 do paciente"
                    @click="openPatientOverview(p)"
                  >
                    {{ p.name.charAt(0).toUpperCase() }}
                  </button>
                  <div>
                    <button
                      type="button"
                      class="text-xs font-medium text-n-slate-12 hover:text-n-brand transition-colors text-left cursor-pointer flex items-center gap-1 group leading-tight"
                      title="Ver histórico e visão 360 do paciente"
                      @click="openPatientOverview(p)"
                    >
                      <span class="group-hover:underline">{{ p.name }}</span>
                      <span class="text-[10px] text-n-brand opacity-0 group-hover:opacity-100 transition-opacity">↗</span>
                    </button>
                    <span v-if="p.document_number" class="text-[10px] text-n-slate-10 font-mono block">CPF: {{ p.document_number }}</span>
                    <span v-else-if="p.birth_date" class="text-[10px] text-n-slate-10 block">Nasc: {{ p.birth_date }}</span>
                  </div>
                </div>
              </td>

              <!-- Status Clínico -->
              <td class="py-2.5 px-3.5">
                <!-- 1. Bloqueado -->
                <div v-if="p.is_blocked" class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30 text-[11px] font-medium" :title="p.blocked_reason">
                  <span class="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
                  <span>Bloqueado</span>
                </div>

                <!-- 2. Alta Clínica Manual -->
                <div v-else-if="p.discharged_at" class="inline-flex flex-col gap-0.5">
                  <span class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30 text-[11px] font-medium">
                    <span class="w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0"></span>
                    <span>Alta Clínica</span>
                  </span>
                  <span v-if="p.discharged_notes" class="text-[10px] text-n-slate-10 truncate max-w-[140px]" :title="p.discharged_notes">
                    {{ p.discharged_notes }}
                  </span>
                </div>

                <!-- 3. Concluído Recente (A Renovar) -->
                <div v-else-if="p.computed_status === 'completed_recent'" class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30 text-[11px] font-medium" title="Finalizou as sessões nos últimos 30 dias (A Renovar)">
                  <span class="w-1.5 h-1.5 rounded-full bg-cyan-500 shrink-0"></span>
                  <span>Concluído (A Renovar)</span>
                </div>

                <!-- 4. Ativo -->
                <div v-else-if="p.computed_status === 'active'" class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-[11px] font-medium">
                  <span class="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"></span>
                  <span>Ativo</span>
                </div>

                <!-- 5. Inativo -->
                <div v-else class="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-n-alpha-2 text-n-slate-11 border border-n-weak text-[11px] font-medium" title="Sem sessões ativas há mais de 30 dias">
                  <span class="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0"></span>
                  <span>Inativo</span>
                </div>
              </td>

              <!-- Contato -->
              <td class="py-2.5 px-3.5 text-n-slate-11">
                <span class="block text-n-slate-12 font-mono font-medium text-xs">{{ p.phone_number || 'Sem telefone' }}</span>
                <span v-if="p.email" class="text-[10px] text-n-slate-10 block truncate max-w-[160px]">{{ p.email }}</span>
              </td>

              <!-- Sessões / Matrículas -->
              <td class="py-2.5 px-3.5">
                <div class="flex flex-col gap-1">
                  <!-- Caso 1: Aluno Matriculado (Recorrente ou Pacote) -->
                  <template v-if="(p.weekly_frequency || p.active_enrollments_count || 0) > 0">
                    <div class="flex items-center gap-2">
                      <span class="px-2 py-0.5 rounded-md bg-n-alpha-1 dark:bg-n-solid-1 text-n-slate-12 border border-n-weak font-semibold font-mono text-[11px]">
                        {{ p.completed_sessions || 0 }} de {{ p.total_sessions || 0 }} {{ p.billing_type === 'recurring' ? 'no mês' : 'sessões' }}
                      </span>
                    </div>
                    <span class="text-[10px] text-n-brand font-medium">
                      📅 {{ p.weekly_frequency || p.active_enrollments_count }}x por semana
                    </span>
                  </template>

                  <!-- Caso 2: Cliente Avulso / Consultas Pontuais -->
                  <template v-else>
                    <span class="px-2 py-0.5 rounded-md bg-n-alpha-1 dark:bg-n-solid-1 text-n-slate-12 border border-n-weak font-semibold font-mono text-[11px] w-fit">
                      {{ p.completed_sessions || 0 }} de {{ p.total_sessions || 0 }} sessões
                    </span>
                    <span class="text-[10px] text-n-slate-10 font-medium">
                      Consulta avulsa
                    </span>
                  </template>
                </div>
              </td>

              <!-- Ações Rápidas (Padrão Chatwoot Next / Linear) -->
              <td class="py-2.5 px-3.5 text-right space-x-1 shrink-0 whitespace-nowrap">
                <!-- Prontuário Clínico (Ação Principal) -->
                <button
                  type="button"
                  class="h-7 px-2.5 rounded-lg bg-brand-subtle hover:bg-brand text-n-brand hover:text-white border border-brand/20 text-xs font-medium transition-all cursor-pointer inline-flex items-center gap-1.5 shadow-2xs"
                  title="Abrir prontuário clínico"
                  @click="openProntuario(p)"
                >
                  <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Prontuário</span>
                </button>

                <!-- Alta Clínica / Reabrir -->
                <button
                  v-if="p.discharged_at"
                  type="button"
                  class="h-7 px-2 rounded-lg bg-n-alpha-1 hover:bg-cyan-500/15 text-cyan-700 dark:text-cyan-300 border border-cyan-500/30 text-xs font-medium transition-colors cursor-pointer inline-flex items-center gap-1"
                  title="Reabrir tratamento clínico"
                  @click="handleReopenTreatment(p)"
                >
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Reabrir</span>
                </button>
                <button
                  v-else
                  type="button"
                  class="h-7 px-2 rounded-lg bg-n-alpha-1 hover:bg-cyan-500/10 text-n-slate-11 hover:text-cyan-600 dark:hover:text-cyan-400 border border-n-weak hover:border-cyan-500/30 text-xs font-medium transition-colors cursor-pointer inline-flex items-center gap-1"
                  title="Registrar Alta Clínica com parecer médico"
                  @click="openDischargeModal(p)"
                >
                  <svg class="w-3 h-3 text-cyan-600 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Alta</span>
                </button>

                <!-- Bloquear / Desbloquear -->
                <button
                  v-if="p.is_blocked"
                  type="button"
                  class="h-7 px-2 rounded-lg bg-n-alpha-1 hover:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30 text-xs font-medium transition-colors cursor-pointer inline-flex items-center gap-1"
                  title="Desbloquear paciente"
                  @click="handleUnblock(p)"
                >
                  <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                  </svg>
                  <span>Desbloquear</span>
                </button>
                <button
                  v-else
                  type="button"
                  class="h-7 px-2 rounded-lg bg-n-alpha-1 hover:bg-rose-500/10 text-n-slate-11 hover:text-rose-600 dark:hover:text-rose-400 border border-n-weak hover:border-rose-500/30 text-xs font-medium transition-colors cursor-pointer inline-flex items-center gap-1"
                  title="Bloquear paciente para não reativar"
                  @click="openBlockModal(p)"
                >
                  <svg class="w-3 h-3 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                  <span>Bloquear</span>
                </button>

                <!-- Editar Cadastro -->
                <button
                  type="button"
                  class="h-7 px-2 rounded-lg bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12 border border-n-weak text-xs font-medium transition-colors cursor-pointer inline-flex items-center gap-1"
                  title="Editar dados cadastrais"
                  @click="openEditPatient(p)"
                >
                  <svg class="w-3 h-3 text-n-slate-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  <span>Editar</span>
                </button>

                <!-- Excluir (Ícone Discreto) -->
                <button
                  type="button"
                  class="h-7 w-7 rounded-lg text-n-slate-10 hover:text-rose-600 hover:bg-rose-500/15 border border-transparent hover:border-rose-500/30 transition-all cursor-pointer inline-flex items-center justify-center"
                  title="Excluir paciente"
                  @click="deletePatient(p)"
                >
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL: Registrar Alta Clínica -->
    <div
      v-if="showDischargeModal && selectedPatient"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-slate-1/80 dark:bg-n-solid-3/80 backdrop-blur-xs animate-fade-in"
      @click.self="showDischargeModal = false"
    >
      <div class="w-full max-w-md bg-n-surface-1 border border-n-weak rounded-xl shadow-2xl overflow-hidden text-n-slate-12 space-y-4 p-6">
        <div class="flex items-center justify-between border-b border-n-weak pb-3">
          <h3 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
            <span>🩺</span>
            <span>Dar Alta Clínica: {{ selectedPatient.name }}</span>
          </h3>
          <button type="button" class="text-n-slate-10 hover:text-n-slate-12 cursor-pointer text-sm" @click="showDischargeModal = false">✕</button>
        </div>

        <p class="text-xs text-n-slate-11 leading-relaxed">
          Ao conceder alta clínica, o paciente é classificado como <strong>Concluído (Alta)</strong> e o parecer médico/fisioterapêutico fica gravado no prontuário.
        </p>

        <div>
          <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
            Parecer de Alta / Evolução Final
          </label>
          <textarea
            v-model="dischargeNotes"
            rows="3"
            placeholder="Ex: Paciente sem queixas de dor na coluna. Amplitude de movimento 100% restabelecida. Alta concedida com orientações posturais."
            class="w-full p-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-cyan-500 focus:outline-none text-n-slate-12 resize-none"
          />
        </div>

        <div class="flex items-center justify-end gap-2 pt-2 border-t border-n-weak">
          <button
            type="button"
            class="h-8 px-3 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 cursor-pointer transition-colors"
            @click="showDischargeModal = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="h-8 px-3.5 text-xs font-medium text-white bg-cyan-600 hover:bg-cyan-500 rounded-lg transition-colors shadow-xs disabled:opacity-50 cursor-pointer flex items-center gap-1"
            :disabled="isSaving"
            @click="handleConfirmDischarge"
          >
            <span>{{ isSaving ? 'Salvando...' : 'Confirmar Alta Clínica' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL: Bloquear / Restringir Paciente -->
    <div
      v-if="showBlockModal && selectedPatient"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-slate-1/80 dark:bg-n-solid-3/80 backdrop-blur-xs animate-fade-in"
      @click.self="showBlockModal = false"
    >
      <div class="w-full max-w-md bg-n-surface-1 border border-n-weak rounded-xl shadow-2xl overflow-hidden text-n-slate-12 space-y-4 p-6">
        <div class="flex items-center justify-between border-b border-n-weak pb-3">
          <h3 class="text-sm font-semibold text-rose-600 dark:text-rose-400 flex items-center gap-2">
            <span>🚫</span>
            <span>Bloquear Paciente: {{ selectedPatient.name }}</span>
          </h3>
          <button type="button" class="text-n-slate-10 hover:text-n-slate-12 cursor-pointer text-sm" @click="showBlockModal = false">✕</button>
        </div>

        <p class="text-xs text-n-slate-11 leading-relaxed">
          Pacientes bloqueados não receberão campanhas automáticas de reativação e a recepção verá um alerta caso tente agendá-lo.
        </p>

        <div>
          <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
            Motivo do Bloqueio
          </label>
          <input
            v-model="blockReason"
            type="text"
            placeholder="Ex: Inadimplência recorrente / Comportamento agressivo"
            class="w-full h-9 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-rose-500 focus:outline-none text-n-slate-12"
          />
        </div>

        <div class="flex items-center justify-end gap-2 pt-2 border-t border-n-weak">
          <button
            type="button"
            class="h-8 px-3 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 cursor-pointer transition-colors"
            @click="showBlockModal = false"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="h-8 px-3.5 text-xs font-medium text-white bg-rose-600 hover:bg-rose-500 rounded-lg transition-colors shadow-xs disabled:opacity-50 cursor-pointer flex items-center gap-1"
            :disabled="isSaving"
            @click="handleConfirmBlock"
          >
            <span>{{ isSaving ? 'Bloqueando...' : 'Confirmar Bloqueio' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Novo / Editar Paciente -->
    <div
      v-if="showModal"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-slate-1/80 dark:bg-n-solid-3/80 backdrop-blur-xs animate-fade-in"
      @click.self="showModal = false"
    >
      <div
        class="w-full max-w-lg bg-n-surface-1 border border-n-weak rounded-xl shadow-2xl overflow-hidden text-n-slate-12"
      >
        <div class="px-6 py-4 border-b border-n-weak flex items-center justify-between bg-n-alpha-1">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ formData.id ? 'Editar Paciente' : 'Cadastrar Novo Paciente' }}
          </h3>
          <button type="button" class="text-n-slate-10 hover:text-n-slate-12 cursor-pointer text-sm" @click="showModal = false">✕</button>
        </div>

        <div class="p-6 space-y-4 max-h-[75vh] overflow-y-auto">
          <div>
            <label class="block text-xs font-medium text-n-slate-12 mb-1">
              Nome Completo <span class="text-rose-500">*</span>
            </label>
            <input
              v-model="formData.name"
              type="text"
              placeholder="Ex: Carlos Eduardo Silva"
              class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12"
            />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-12 mb-1">Telefone / WhatsApp</label>
              <input
                v-model="formData.phone_number"
                type="text"
                placeholder="+55 11 99999-9999"
                class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-12 mb-1">E-mail</label>
              <input
                v-model="formData.email"
                type="email"
                placeholder="paciente@email.com"
                class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12"
              />
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-n-slate-12 mb-1">CPF / RG</label>
              <input
                v-model="formData.document_number"
                type="text"
                placeholder="000.000.000-00"
                class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12"
              />
            </div>
            <div>
              <label class="block text-xs font-medium text-n-slate-12 mb-1">Data de Nascimento</label>
              <input
                v-model="formData.birth_date"
                type="date"
                class="w-full h-8 px-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12"
              />
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-n-slate-12 mb-1">Prontuário Clínico / Histórico Inicial</label>
            <textarea
              v-model="formData.medical_record"
              rows="3"
              placeholder="Alergias, queixas principais, histórico de saúde..."
              class="w-full p-2.5 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12 resize-none"
            />
          </div>
        </div>

        <div class="px-6 py-3.5 border-t border-n-weak bg-n-alpha-1 flex items-center justify-end gap-2.5">
          <button type="button" class="h-8 px-3 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 cursor-pointer transition-colors" @click="showModal = false">
            Cancelar
          </button>
          <button
            type="button"
            class="h-8 px-3.5 text-xs font-medium text-white bg-brand hover:bg-brand-hover rounded-lg shadow-xs cursor-pointer disabled:opacity-50 transition-colors"
            :disabled="isSaving"
            @click="savePatient"
          >
            {{ isSaving ? 'Salvando...' : 'Salvar Paciente' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal Prontuário Clínico & Evoluções -->
    <div
      v-if="showProntuarioModal && selectedPatient"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-slate-1/80 dark:bg-n-solid-3/80 backdrop-blur-xs animate-fade-in"
      @click.self="showProntuarioModal = false"
    >
      <div
        class="w-full max-w-2xl bg-n-surface-1 border border-n-weak rounded-xl shadow-2xl overflow-hidden text-n-slate-12 flex flex-col max-h-[85vh]"
      >
        <div class="px-6 py-4 border-b border-n-weak flex items-center justify-between bg-n-alpha-1">
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12 flex items-center gap-2">
              <span>📋</span>
              <span>Prontuário Clínico: {{ selectedPatient.name }}</span>
            </h3>
            <p class="text-xs text-n-slate-11">
              Histórico de atendimentos, altas médicas e anotações clínicas do paciente.
            </p>
          </div>
          <button type="button" class="text-n-slate-10 hover:text-n-slate-12 cursor-pointer text-sm" @click="showProntuarioModal = false">✕</button>
        </div>

        <div class="p-6 overflow-y-auto space-y-6 flex-1">
          <!-- Alerta se estiver com Alta Clínica -->
          <div v-if="selectedPatient.discharged_at" class="p-3.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30 space-y-1">
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-cyan-800 dark:text-cyan-300 flex items-center gap-1.5">
                <span>🔵</span>
                <span>Alta Clínica Concedida em {{ new Date(selectedPatient.discharged_at).toLocaleDateString('pt-BR') }}</span>
              </span>
              <button
                type="button"
                class="text-[11px] font-semibold text-cyan-700 dark:text-cyan-300 underline cursor-pointer hover:text-cyan-900 dark:hover:text-white"
                @click="handleReopenTreatment(selectedPatient)"
              >
                Reabrir Tratamento
              </button>
            </div>
            <p v-if="selectedPatient.discharged_notes" class="text-xs text-n-slate-11">
              {{ selectedPatient.discharged_notes }}
            </p>
          </div>

          <div>
            <label class="block text-xs font-semibold text-n-slate-12 mb-1.5">
              Prontuário Geral / Anotações Permanentes
            </label>
            <textarea
              v-model="selectedPatient.medical_record"
              rows="4"
              placeholder="Histórico clínico, doenças pré-existentes, alergias, medicações de uso contínuo..."
              class="w-full p-3 text-xs bg-n-alpha-1 dark:bg-n-solid-1 border border-n-weak rounded-lg focus:border-brand focus:outline-none text-n-slate-12"
            />
          </div>

          <div>
            <h4 class="text-xs font-semibold text-n-slate-12 uppercase tracking-wider mb-3">
              Histórico de Sessões & Evoluções
            </h4>

            <div v-if="!selectedPatient.appointments_history || selectedPatient.appointments_history.length === 0" class="text-xs text-n-slate-10 py-6 text-center bg-n-alpha-1 rounded-lg border border-n-weak">
              Nenhuma sessão realizada até o momento.
            </div>

            <div v-else class="space-y-3">
              <div
                v-for="app in selectedPatient.appointments_history"
                :key="`${app.type}-${app.id}`"
                class="p-3.5 rounded-lg bg-n-surface-1 border border-n-weak space-y-1.5"
              >
                <div class="flex items-center justify-between text-xs">
                  <span class="font-semibold text-n-slate-12">{{ app.service_name }}</span>
                  <span class="text-n-slate-10 font-mono">{{ app.date }} {{ app.time_slot ? `às ${app.time_slot}` : '' }}</span>
                </div>
                <div class="text-[11px] text-n-slate-11">
                  Profissional: <strong class="text-n-slate-12">{{ app.professional_name }}</strong>
                  <template v-if="app.session_number">
                    | Sessão: <strong class="text-n-brand">{{ app.session_number }}{{ app.total_sessions ? `/${app.total_sessions}` : '' }}</strong>
                  </template>
                </div>
                <p v-if="app.prontuario_notes" class="text-xs text-n-slate-11 pt-1.5 border-t border-n-weak whitespace-pre-wrap">
                  <span class="text-n-slate-10 font-semibold block text-[11px] mb-0.5">Evolução Clínica:</span>
                  {{ app.prontuario_notes }}
                </p>
                <p v-else class="text-[11px] text-n-slate-9 italic pt-1 border-t border-n-weak">
                  Presença confirmada sem anotações de evolução.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div class="px-6 py-3.5 border-t border-n-weak bg-n-alpha-1 flex items-center justify-end gap-2.5">
          <button type="button" class="h-8 px-3 text-xs font-medium text-n-slate-11 hover:text-n-slate-12 cursor-pointer transition-colors" @click="showProntuarioModal = false">
            Fechar
          </button>
          <button
            type="button"
            class="h-8 px-3.5 text-xs font-medium text-white bg-brand hover:bg-brand-hover rounded-lg shadow-xs cursor-pointer disabled:opacity-50 transition-colors"
            :disabled="isSaving"
            @click="saveProntuarioChanges"
          >
            {{ isSaving ? 'Salvando...' : 'Salvar Prontuário' }}
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL DE VISÃO GERAL 360 DO PACIENTE (HISTÓRICO, PRONTUÁRIO, MATRÍCULAS) -->
    <PatientOverviewModal
      :show="showOverviewModal"
      :patient-data="overviewPatientData"
      @close="showOverviewModal = false"
      @edit-patient="handleOverviewEditPatient"
      @discharge-patient="handleOverviewDischarge"
      @block-patient="handleOverviewBlock"
      @reopen-treatment="handleReopenTreatment"
      @unblock-patient="handleUnblock"
      @updated="handleOverviewUpdated"
    />
  </div>
</template>
